import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Post } from "src/api/client-api/post/postSchema";
import { useGetIngredientsPosts } from "src/api/client-api/post/useGetIngredientsPosts";
import { useGetProductPost } from "src/api/client-api/post/useGetProductPost";
import { IngredientPost } from "src/api/client-api/post/useGetSomeIngredientPosts";
import {
    ProductReferenceScores,
    useGetProductReferenceScores,
} from "src/api/client-api/productReferenceScores/useGetProductReferenceScores";
import { Product, useGetOneProduct } from "src/api/client-api/products/useGetOneProduct";
import { IngredientReference } from "src/api/sure-api/ingredientReference/useGetAllIngredientReference";
import { useGetSomeIngredientReference } from "src/api/sure-api/ingredientReference/useGetSomeIngredientReference";
import { SsqList } from "src/api/sure-api/ssq/useGetAllSsq";
import { Ssq, useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { useGetSomeSsq } from "src/api/sure-api/ssq/useGetSomeSsq";
import { ScoreService, ScoreVariation } from "src/context/EntityBasedScore/ScoreService";
import { Dimension } from "src/context/EntityBasedScore/useDimensions";
import { ScoreState, useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { SelectVariationCallback } from "src/context/EntityBasedScore/useVariations";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { DIMENSION as QUESTIONNAIRE_DIMENSION } from "src/pages/Product/ProductDetails/Questionnaire/Questionnaire";
import { ProductReference } from "src/api/sure-api/useProductReference";

type ProductContextInitialValues = {
    product: Product | undefined;
    productReferenceScores: ProductReferenceScores;
    variations: Record<number, number>;
    setVariations: (variations: Record<number, number>) => void;
    isLoading: boolean;
    isRefetching: boolean;

    ssq: Ssq | undefined;
    isSsqLoading: boolean;
    isMonolithic: boolean | undefined;

    ingredientReferences: IngredientReference[] | undefined;
    ssqIngredientReferences: SsqList | undefined;
    isSsqLoadingIngredientReferences: boolean;

    post: Post | undefined;
    ingredientPosts: IngredientPost[];
    isPostLoading: boolean;
    allScoreWithVariations: ScoreVariation[];
    scoreState: ScoreState;
    productScores: Score[];
    switchToDimension: (newDimension: Dimension) => void;
    selectVariation: SelectVariationCallback;
    selectedScore: ScoreVariation;
    productReference?: ProductReference | undefined;
};

const ProductContext = createContext<ProductContextInitialValues | null>(null);

type ProductContextProviderProps = { children: ReactNode };
export const ProductContextProvider = (props: ProductContextProviderProps) => {
    const { children } = props;
    const { productId } = useParams();
    const [variations, setVariations] = useState<Record<number, number>>({});
    const { selectedCompany, currentCampaign, getScoresByType } = useFoodpilotContext();

    const entityParams = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
        initDimensions: {
            dimensions: [["main"]],
        },
        initVariations: {
            variations,
        },
    });

    const { product, isLoading, isRefetching } = useGetOneProduct(productId ?? "A completely bad ID", {
        campaign: currentCampaign?.id,
        company_id: selectedCompany?.id,
        scoreQuery: entityParams.query,
        variations: variations,
        dimensions: entityParams.state.dimensions,
    });

    const ingredientIds = (product?.productIngredients ?? []).map((pi) => pi.ingredient.id);
    const campaignId = currentCampaign?.id;
    const datas = useGetIngredientsPosts(ingredientIds, campaignId);
    const { productReferenceScores = [] } = useGetProductReferenceScores(productId ?? "0");

    const ssqId = product?.ssqId;
    const irid = product?.productIngredients
        ?.map((pi) => pi.ingredient.ingredientReferenceId)
        .filter((i) => i !== undefined);
    const { data: ingredientReferences } = useGetSomeIngredientReference(irid);

    const { ssq, isLoading: isSsqLoading } = useGetOneSsq(ssqId);
    const isMonolithic = ssq?.monolithic;

    const ssqIds = ingredientReferences
        ?.map((ir) => ir.ssq)
        .filter((s) => s !== undefined)
        .map((s) => s.id);
    const { ssq: ssqIngredientReferences, isLoading: isSsqLoadingIngredientReferences } = useGetSomeSsq(ssqIds);

    const { post, isLoading: isPostLoading } = useGetProductPost(
        product?.id,
        selectedCompany?.id ?? null,
        currentCampaign?.id,
    );

    const productScores = getScoresByType("product");

    const allScoreWithVariations = ScoreService.getScoreList(
        productScores,
        product?.scores,
        (scoreId, scoreVariationId) => {
            entityParams.state.selectVariation(scoreId, scoreVariationId);
        },
        entityParams.state.lastSelectedVariation,
    );

    useEffect(() => {
        if (isMonolithic !== false) return;

        const dimensions = entityParams.state.dimensions;
        if (dimensions.flat().includes(QUESTIONNAIRE_DIMENSION)) return;

        const lastSelectedScoreId = entityParams.state.lastSelectedVariation?.scoreId;
        if (lastSelectedScoreId === undefined) return;

        entityParams.state.selectVariation(lastSelectedScoreId, null);
    }, [isMonolithic, isRefetching]);

    const items: ProductContextInitialValues = {
        product,
        productReferenceScores,
        isLoading,
        isRefetching,
        ssq,
        isSsqLoading,
        isMonolithic,
        post,
        isPostLoading,
        ssqIngredientReferences,
        isSsqLoadingIngredientReferences,
        ingredientReferences,
        variations,
        setVariations,
        allScoreWithVariations: allScoreWithVariations.scoresWithVariations,
        selectedScore: allScoreWithVariations.lastSelected,
        productScores,
        scoreState: entityParams.state,
        switchToDimension: entityParams.state.switchToDimension,
        selectVariation: entityParams.state.selectVariation,
        ingredientPosts: datas.every((d) => d.data !== undefined) ? datas.map((d) => d.data.data).flat() : [],
    };

    return <ProductContext.Provider value={items}>{children}</ProductContext.Provider>;
};

export const useProductContext = () => {
    const context = useContext(ProductContext);

    if (context) {
        return context;
    }

    throw new Error(`useProductContext must be used within an ProductContextProvider`);
};
